import { redirectTo } from '../../shared/navigation';
import type { TokenPair } from '../api/types';
import { config } from '../config';
import { http } from '../http/client';
import { tokenStorage } from './tokenStorage';

/**
 * Customer SSO for the Platform's own session (Card-Plan redirect SSO: the `architech_sso`
 * cookie → `/auth/sso/authorize` → `launch_code`).
 *
 * The Platform session lives in sessionStorage, which is per tab. A tab that never signed in
 * here — a business page opened in a new tab, or a tab that came back from Cosmetics or
 * Restaurant — therefore starts signed out although the browser still has a customer SSO
 * session. Boot asks the API whether it has one and, if so, makes one silent round trip that
 * turns it into this tab's own Platform session. Each tab gets its own refresh token this way;
 * a refresh token is never copied between tabs.
 *
 * The keys are the cart adapter's (`features/public/PlatformCart.tsx`), so a sign-in the cart
 * started and a check started here verify each other's answers.
 */
const KEY = 'architech.customer.platform';
const STATE_KEY = `${KEY}.state`;
/** Read by the cart adapter: once set, it never starts a silent check of its own in this tab. */
const CHECKED_KEY = `${KEY}.checked`;
/** A silent check left this tab and no answer came back yet (an error page, or another navigation won). */
const PENDING_KEY = 'architech.platform.v1.ssoPending';
const ANSWER_PARAMS = ['launch_code', 'state', 'sso'];
/** The visitor is signing in by hand on these; a silent round trip would only get in the way. */
const SIGN_IN_PATHS = ['/login', '/register', '/org-select'];

export const ssoAuthorizeApi: string = import.meta.env.VITE_SSO_API_URL
  || (import.meta.env.DEV ? 'http://localhost:5100/api/v1/platform' : new URL(config.apiBaseUrl, window.location.origin).href);

/** Overridable in tests; jsdom cannot navigate. */
export const ssoNavigation = { assign: (url: string) => redirectTo(url) };

interface SsoAnswer {
  /** Null for `sso=none`: the browser had no customer SSO session. */
  launchCode: string | null;
  /** The return address the launch code is bound to. */
  redirectUri: string;
}

function read(key: string): string | null {
  try { return window.sessionStorage.getItem(key); } catch { return null; }
}

function remove(key: string): void {
  try { window.sessionStorage.removeItem(key); } catch { /* storage blocked */ }
}

/**
 * Removes an SSO answer (`?launch_code=&state=` or `?sso=none&state=`) from the address bar.
 * Returns undefined when there is none, and null when its state is not the one this tab sent.
 */
function takeAnswer(): SsoAnswer | null | undefined {
  const url = new URL(window.location.href);
  const state = url.searchParams.get('state');
  if (!state && url.searchParams.get('sso') !== 'none') return undefined;
  const launchCode = url.searchParams.get('launch_code');
  const expected = read(STATE_KEY);
  remove(STATE_KEY);
  remove(PENDING_KEY);
  ANSWER_PARAMS.forEach((name) => url.searchParams.delete(name));
  window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
  return expected && state === expected ? { launchCode, redirectUri: url.href } : null;
}

async function adopt(answer: SsoAnswer & { launchCode: string }): Promise<void> {
  const { data } = await http().post<TokenPair>('/auth/product-launch/exchange',
    { code: answer.launchCode, productCode: 'PLATFORM', redirectUri: answer.redirectUri },
    { skipErrorToast: true, skipAuthRefresh: true });
  const claims = JSON.parse(atob(data.accessToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))) as { tid?: string };
  if (!claims.tid) throw new Error('The customer session names no organization.');
  tokenStorage.applyTokenPair(data, { organizationId: claims.tid, productCode: 'PLATFORM' });
}

async function hasCustomerSession(): Promise<boolean> {
  try {
    // Pages wait for boot before leaving on load (see whenSessionBooted), so do not hold them for long.
    const { data } = await http().get<{ signedIn?: boolean }>('/auth/sso/status', { skipErrorToast: true, skipAuthRefresh: true, timeout: 5_000 });
    return data?.signedIn === true;
  } catch {
    return false;
  }
}

/**
 * For a tab with no Platform session: adopts the answer to a check this tab started, or starts
 * one when the browser still has a customer SSO session. 'redirecting' means the browser is
 * leaving for that check.
 */
export async function signInFromCustomerSso(): Promise<'adopted' | 'redirecting' | 'none'> {
  // Boot owns the check in this tab, so the cart adapter must not start a second one.
  try { window.sessionStorage.setItem(CHECKED_KEY, '1'); } catch { return 'none'; }
  const answer = takeAnswer();
  if (answer !== undefined) {
    if (!answer?.launchCode) return 'none';
    try {
      await adopt({ ...answer, launchCode: answer.launchCode });
      return 'adopted';
    } catch {
      return 'none';
    }
  }
  if (navigator.webdriver || SIGN_IN_PATHS.includes(window.location.pathname)) return 'none';
  // The last check never came back: skip this one load so Back from an error page cannot loop.
  if (read(PENDING_KEY)) { remove(PENDING_KEY); return 'none'; }
  if (!(await hasCustomerSession())) return 'none';
  const state = crypto.randomUUID();
  try {
    window.sessionStorage.setItem(STATE_KEY, state);
    window.sessionStorage.setItem(PENDING_KEY, '1');
  } catch {
    return 'none';
  }
  const redirect = new URL(window.location.href);
  ANSWER_PARAMS.forEach((name) => redirect.searchParams.delete(name));
  redirect.hash = '';
  ssoNavigation.assign(`${ssoAuthorizeApi}/auth/sso/authorize?${new URLSearchParams({ product: 'PLATFORM', redirect_uri: redirect.href, state, prompt: 'none' })}`);
  return 'redirecting';
}

/** Ends the browser's customer SSO session, or the next new tab would sign straight back in. */
export async function endCustomerSso(): Promise<void> {
  remove(KEY);
  await http().post('/auth/sso/logout', null, { headers: { 'X-Customer-Sso': '1' }, skipErrorToast: true, skipAuthRefresh: true });
}
